// deps
import React, { memo, useCallback, useMemo } from "react";

// internal deps
import { NETBANKING_PAYMENT_METHOD_CODE } from "@swiggy-private/payment-core";

// interfaces
import {
    INetBankingPaymentGroup,
    NetBankingPaymentGroupMeta,
    NetBankingPaymentMethodData,
} from "@payment-ui/interfaces/NetBanking";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodIconView as NbIconView } from "@payment-ui/widgets/payment-method-views/icon-view";
import { PaymentMethodListView as NbListView } from "@payment-ui/widgets/payment-method-views/list-view";
import { IconListView as NbIconListView } from "@payment-ui/widgets/payment-method-views/icon-list-view";

// helpers
import { canEnablePaymentMethod } from "@payment-ui/helpers/Payment";

// test-ids
import { NETBANKING_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { format } from "@swiggy-private/core-ui";
import { TEXTS } from "@payment-ui/constants/index";

interface Props extends PaymentMethodProps {
    key: string;
    methodData: NetBankingPaymentMethodData;
    paymentActions: INetBankingPaymentGroup["paymentActions"];
    paymentAmount: string;
    paymentGroupMeta?: NetBankingPaymentGroupMeta;
    isPreferredMethod?: boolean;
    showListView?: boolean;
}

function NetBankingPaymentMethod(props: Props): React.ReactElement {
    const {
        methodData,
        isPreferredMethod,
        isPaymentGroupEnabled,
        paymentActions,
        paymentGroupMeta,
        selected,
        showListView,
    } = props;

    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(methodData, isPaymentGroupEnabled), [
        isPaymentGroupEnabled,
        methodData,
    ]);

    const paymentAmount = methodData.payableAmount ?? props.paymentAmount;

    // TODO: Temporarily disabled untill changes from backend are made, will need modification.
    const ctaText = useMemo(() => (isPreferredMethod ? format(TEXTS.PAY_X, paymentAmount) : ""), [
        isPreferredMethod,
        paymentAmount,
    ]);

    const onPay = useCallback((): void => {
        if (!isMethodEnabled) {
            return;
        }

        paymentActions.onPay(methodData, {
            bankCode: methodData.nbCode,
            bankName: methodData.name,
            merchantId: paymentGroupMeta?.merchantId,
            paymentMethodType: NETBANKING_PAYMENT_METHOD_CODE,
        });
    }, [isMethodEnabled, paymentActions, methodData, paymentGroupMeta]);

    const onClick = useCallback((): void => {
        if (!isMethodEnabled) {
            return;
        }

        paymentActions.onSelect(methodData);

        // Preferred method renders in list view. onpay should be called only in icon view on click.
        if (!isPreferredMethod) {
            onPay();
        }
    }, [isMethodEnabled, paymentActions, methodData, isPreferredMethod, onPay]);

    if (!isPreferredMethod) {
        return showListView ? (
            <NbIconListView
                methodData={methodData}
                isMethodEnabled={isMethodEnabled}
                testIds={NETBANKING_PAYMENT_METHOD_TEST_IDS}
                onClick={onPay}
            />
        ) : (
            <NbIconView
                methodData={methodData}
                isMethodEnabled={isMethodEnabled}
                testIds={NETBANKING_PAYMENT_METHOD_TEST_IDS}
                onClick={onClick}
            />
        );
    }

    return (
        <NbListView
            methodData={methodData}
            isMethodEnabled={isMethodEnabled}
            testIds={NETBANKING_PAYMENT_METHOD_TEST_IDS}
            onClick={onClick}
            onPayButtonClick={onPay}
            selected={selected}
            payCtaMsg={ctaText}
        />
    );
}

export default memo(NetBankingPaymentMethod);
